import {
  AnimationAction,
  AnimationMixer,
  Mesh,
  Object3D,
  LoopOnce,
  LoopRepeat,
  AnimationClip,
  InterpolateLinear,
  KeyframeTrack,
  NumberKeyframeTrack,
} from "three";
import Root from "../../../app";
import { AvatarConfig, RangeMappingConfig, FilterConfig, VrmFilterConfig, NaturalDownmixConfig } from "../player/type";
import { transposeArray, normalizeBlendshapeData, adjustBlendshapesToModel } from "./utils";
import { STANDARD_ARKIT_BLENDSHAPE_COUNT, SERVER_CHANNEL_NAMES } from "./constants";
import {
  createNaturalState,
  processNaturalChunk,
  resetCausalState,
  DEFAULT_NATURAL_PARAMS,
  NaturalDownmixState,
} from "./natural/pipeline";
import { DEFAULT_NORM_PRIORS } from "./natural/norm-tracker";
import { getNaturalRuntimeOverrides } from "./natural/runtime-overrides";

export default class LipSync {

  //---- Instances ----
  root: Root;

  //---- Avatar ----
  protected avatar: Object3D;
  protected avatarConfig: AvatarConfig;
  private morphMeshes: Mesh[] = [];

  //---- Mixer ----
  protected mixer: AnimationMixer;
  protected fps: number;
  protected delta: number;

  //---- Animation ----
  protected prevAnimation: AnimationAction | null = null;
  protected currAnimation: AnimationAction | null = null;

  //---- Natural Downmix (v18m 파이프라인, Bobbi류 카드 리그) ----
  private naturalState: NaturalDownmixState | null = null;
  private naturalWarnedMissingTarget = false;

  //---- Idle Blink (발화 사이 대기 중 깜빡임 — vrm_filter/natural_downmix 아바타 전용) ----
  private idleBlinkAction: AnimationAction | null = null;
  private idleBlinkClip: AnimationClip | null = null;
  private onSpeechClipFinished = (e: { action: AnimationAction }) => {
    if (e.action === this.currAnimation) this.startIdleBlink();
  };

  constructor(root: Root, avatar: Object3D, mixer: AnimationMixer, fps: number, delta: number, avatarConfig: AvatarConfig) {
    this.root = root;
    this.avatar = avatar;
    this.mixer = mixer;
    this.fps = fps;
    this.delta = delta;
    this.avatarConfig = avatarConfig;

    if (this.avatar) {
      this.avatar.traverse((child: Object3D | Mesh) => {
        if ((child as Mesh).morphTargetInfluences || (child as Mesh).morphTargetDictionary) {
          this.morphMeshes.push(child as Mesh);
        }
      });
    }

    // Neutral 핀: 립싱크 액션이 없을 때도 Neutral 카드가 1로 남도록 지금 설정한다.
    // three.js PropertyMixer 는 첫 바인딩 시점 값을 "원래 상태"로 캡처하므로
    // 여기서 1로 두면 클립 페이드아웃 후에도 1로 복원된다.
    const lipsyncCfg = avatarConfig?.lipsync;
    if (lipsyncCfg?.processingType === 'natural_downmix') {
      const cfg = lipsyncCfg.config as NaturalDownmixConfig | undefined;
      const neutralIdx = cfg?.targets?.neutralMorphIndex;
      if (cfg && cfg.pinNeutral !== false && neutralIdx !== undefined) {
        const mouthMesh = this.findMeshesByName(cfg.targets.mouthMeshName)
          .find((m) => m.morphTargetInfluences && neutralIdx < m.morphTargetInfluences.length);
        if (mouthMesh?.morphTargetInfluences) {
          mouthMesh.morphTargetInfluences[neutralIdx] = 1;
        }
      }
    }

    // 유휴 깜빡임: 발화 클립이 끝나면 재개, play()가 호출되면 중단
    this.mixer.addEventListener('finished', this.onSpeechClipFinished as never);
    this.startIdleBlink();
  }

  // ─────────────────────── Idle Blink ───────────────────────

  /** 깜빡임을 구동할 (메시, 모프 인덱스) 바인딩 목록 — 설정 방식별로 해석 */
  private getBlinkBindings(): Array<{ mesh: Mesh; index: number }> {
    const lipsync = this.avatarConfig?.lipsync;
    const bindings: Array<{ mesh: Mesh; index: number }> = [];

    if (lipsync?.processingType === 'vrm_filter') {
      // 이름 기반: morphTargetDictionary 에서 eyeBlink 모프를 찾는다
      this.morphMeshes.forEach((mesh) => {
        const dict = mesh.morphTargetDictionary;
        if (!dict || !mesh.morphTargetInfluences) return;
        ['eyeBlinkLeft', 'eyeBlinkRight'].forEach((name) => {
          const idx = dict[name];
          if (idx !== undefined && idx < mesh.morphTargetInfluences!.length) {
            bindings.push({ mesh, index: idx });
          }
        });
      });
    } else if (lipsync?.processingType === 'natural_downmix') {
      const cfg = lipsync.config as NaturalDownmixConfig | undefined;
      const targets = cfg?.targets;
      if (cfg?.blinkEnabled && targets?.blinkMeshName && targets.blinkMorphIndices?.length) {
        this.findMeshesByName(targets.blinkMeshName).forEach((mesh) => {
          if (!mesh.morphTargetInfluences) return;
          targets.blinkMorphIndices!.forEach((index) => {
            if (index < mesh.morphTargetInfluences!.length) bindings.push({ mesh, index });
          });
        });
      }
    }
    // filter/range_mapping 레거시 아바타는 깜빡임 정보가 없으므로 기존 동작 유지 (미적용)
    return bindings;
  }

  /** 대기 중 깜빡임 루프 시작 (약 4.3초마다 1회, 발화 중에는 중단) */
  private startIdleBlink(): void {
    if (this.idleBlinkAction) return; // 이미 재생 중

    if (!this.idleBlinkClip) {
      const bindings = this.getBlinkBindings();
      if (!bindings.length) return;

      const fps = 30;
      const loopSec = 4.3;
      const frameCount = Math.round(loopSec * fps);
      const values = new Array<number>(frameCount).fill(0);
      // 루프 후반부에 1회 깜빡임 (약 170ms 벨 커브, 완전히 감김)
      const kernel = [0.35, 0.9, 1, 0.9, 0.35];
      const start = frameCount - 12;
      kernel.forEach((k, i) => { values[start + i] = k; });
      const times = values.map((_, i) => i / fps);

      const tracks = bindings.map(({ mesh, index }) => new NumberKeyframeTrack(
        `${mesh.name}.morphTargetInfluences[${index}]`,
        times, values, InterpolateLinear
      ));
      this.idleBlinkClip = new AnimationClip('idle_blink', -1, tracks);
    }

    const action = this.mixer.clipAction(this.idleBlinkClip);
    action.setLoop(LoopRepeat, Infinity);
    action.reset().fadeIn(0.15).play();
    this.idleBlinkAction = action;
  }

  private stopIdleBlink(): void {
    if (!this.idleBlinkAction) return;
    this.idleBlinkAction.stop();
    this.idleBlinkAction = null;
  }

  /**
   * GLTFLoader 는 노드/메시 이름을 sanitize(공백→_, "[].:/" 제거)하고 중복 시 _N 접미사를
   * 붙인다 — 예: 'Circle.002' 노드(프리미티브 3개)의 자식 메시는 'Circle002_1'~'_3'.
   * 원본 glTF 이름으로도 런타임 메시를 찾을 수 있도록 두 표기를 모두 매칭한다.
   */
  private findMeshesByName(targetName: string): Mesh[] {
    const sanitized = targetName.replace(/\s/g, '_').replace(/[\[\]\.:\/]/g, '');
    return this.morphMeshes.filter((mesh) => {
      if (mesh.name === targetName || mesh.name === sanitized) return true;
      return mesh.name.replace(/_\d+$/, '') === sanitized;
    });
  }

  play(blendshape: number[][], options?: Partial<{ fps: number, duration: number }>): void {
    const defaultOptions = { fps: 30, duration: 0.2, ...options };

    // 발화 중에는 유휴 깜빡임 중단 (발화 클립의 blink 채널/스케줄이 대신 구동)
    this.stopIdleBlink();

    // 데이터 기반 blendshape 처리
    const clip = this.createBlendshapeClip(this.morphMeshes, blendshape, defaultOptions.fps);
    const action = this.mixer.clipAction(clip);
    action.setLoop(LoopOnce, 1);
    action.clampWhenFinished = true;

    if (this.prevAnimation) {
      this.mixer.uncacheAction(this.prevAnimation.getClip());
      this.mixer.uncacheClip(this.prevAnimation.getClip());
    }

    if (this.currAnimation) {
      this.prevAnimation = this.currAnimation;
      this.currAnimation.fadeOut(defaultOptions.duration);
    }

    this.currAnimation = action;
    this.currAnimation.reset().fadeIn(defaultOptions.duration).play();
  }

  stop(): void {
    // 발화 종료/인터럽트 — 다음 발화가 깨끗하게 시작하도록 인과 상태 리셋
    // (노름 통계는 세션 내 유지 — currAnimation 가드보다 먼저 실행돼야 함)
    if (this.naturalState) resetCausalState(this.naturalState);
    if (!this.currAnimation) return;
    this.currAnimation.stop();
    // 발화 중단(인터럽트) 후 대기 상태 — 유휴 깜빡임 재개
    this.startIdleBlink();
  }

  resume(): void {
    if (!this.currAnimation) return;
    this.currAnimation.paused = false;
  }

  pause(): void {
    if (!this.currAnimation) return;
    this.currAnimation.paused = true;
  }

  dispose(): void {
    this.naturalState = null;

    this.mixer?.removeEventListener('finished', this.onSpeechClipFinished as never);
    this.stopIdleBlink();
    if (this.mixer && this.idleBlinkClip) {
      this.mixer.uncacheClip(this.idleBlinkClip);
      this.idleBlinkClip = null;
    }

    if (this.mixer && this.currAnimation) {
      this.mixer.uncacheAction(this.currAnimation.getClip());
      this.mixer.uncacheClip(this.currAnimation.getClip());
    }

    this.morphMeshes.forEach((child) => {
      child.clear();
    });

    this.morphMeshes = [];
  }

  private createBlendshapeClip(meshes: Mesh[], blendshape: number[][], fps: number): AnimationClip {
    const lipsync = this.avatarConfig.lipsync;

    // vrm_filter: 확장 채널(U/O 등)까지 사용하므로 서버 전체 채널 폭으로 정규화한 뒤
    // 이름 기반으로 모델 모프에 매핑해 풀 페이스(입+눈썹+표정+깜빡임+ㅜㅗ) 재생
    if (lipsync?.processingType === 'vrm_filter') {
      const wideBlendshape = normalizeBlendshapeData(blendshape, SERVER_CHANNEL_NAMES.length);
      return this.createVrmFilterClip(wideBlendshape, fps, lipsync.config as VrmFilterConfig | undefined, lipsync.targetMeshNames);
    }

    // [Step 1] Voice Server 데이터 정규화 (그 외 경로는 표준 ARKit 52채널만 사용)
    const normalizedBlendshape = normalizeBlendshapeData(blendshape, STANDARD_ARKIT_BLENDSHAPE_COUNT);

    // natural_downmix: v18m 자연스러움 파이프라인 + 2카드 다운믹스 (Bobbi류 카드 리그)
    if (lipsync?.processingType === 'natural_downmix') {
      return this.createNaturalDownmixClip(normalizedBlendshape, fps, lipsync.config as NaturalDownmixConfig);
    }

    // [Step 2] processingType에 따른 처리
    let processedBlendshape: number[][] = normalizedBlendshape;

    if (lipsync?.processingType === 'range_mapping') {
      processedBlendshape = this.processRangeMapping(normalizedBlendshape, lipsync?.config as RangeMappingConfig);
    } else if (lipsync?.processingType === 'filter') {
      processedBlendshape = this.processFilter(normalizedBlendshape, lipsync?.config as FilterConfig);
    }

    const tracks: KeyframeTrack[] = [];
    const frameCount = processedBlendshape.length;
    const times = processedBlendshape.map((_, index) => index / fps);
    const transposedBlendShapes = transposeArray(processedBlendshape);

    // 타겟 메시 필터링
    const targetMeshNames = lipsync?.targetMeshNames;
    const filteredMeshes = targetMeshNames
      ? meshes.filter(mesh => targetMeshNames.some(name => mesh.name.includes(name)))
      : meshes;

    // 인덱스 오프셋
    const indexOffset = lipsync?.indexOffset ?? 0;

    filteredMeshes.forEach((mesh: Mesh) => {
      const morphTargetInfluences = mesh.morphTargetInfluences;

      if (!morphTargetInfluences) return;

      const morphTargetCount = morphTargetInfluences.length;

      // [Step 3] 인덱스 오프셋 적용 (Voice Server 인덱스 -> 모델 인덱스)
      let adjustedTransposed = transposedBlendShapes;
      if (indexOffset !== 0) {
        // 인덱스를 시프트 (예: offset=-1이면 데이터를 한 칸씩 앞으로)
        adjustedTransposed = [];
        for (let i = 0; i < transposedBlendShapes.length; i++) {
          const targetIndex = i + indexOffset;
          if (targetIndex >= 0 && targetIndex < transposedBlendShapes.length) {
            adjustedTransposed[targetIndex] = transposedBlendShapes[i];
          }
        }
        // 빈 인덱스 채우기
        for (let i = 0; i < transposedBlendShapes.length; i++) {
          if (!adjustedTransposed[i]) {
            adjustedTransposed[i] = Array(frameCount).fill(0);
          }
        }
      }

      // [Step 4] 모델의 morphTarget 개수에 맞게 데이터 조정
      const adjustedBlendShapes = adjustBlendshapesToModel(
        adjustedTransposed,
        morphTargetCount,
        frameCount
      );

      // [Step 5] KeyframeTrack 생성
      adjustedBlendShapes.forEach((values, index) => {
        const track = new NumberKeyframeTrack(
          `${mesh.name}.morphTargetInfluences[${index}]`,
          times,
          values,
          InterpolateLinear
        );
        tracks.push(track);
      });
    });

    return new AnimationClip("animation", -1, tracks);
  }

  /**
   * v18m 자연스러움 파이프라인을 거친 2카드(jawOpen/mouthClose) + 깜빡임 트랙 생성.
   * 설정된 타겟 모프 외에는 트랙을 만들지 않아 다른 시스템과 충돌하지 않는다.
   */
  /**
   * vrm_filter: 서버 52채널을 모델 모프에 직접 매핑해 풀 페이스 재생.
   * - config.mappingTable 이 있으면 테이블(서버 채널 idx → 모델 모프 idx) 사용 (vroid 방식)
   * - 없으면 각 메시의 morphTargetDictionary 에서 ARKit 채널명을 찾아 자동 매핑
   *   (Taekwondo15 처럼 모프 이름이 ARKit 표준명인 VRM은 설정 없이 동작)
   * 프리미티브 분할(Face_1~_3)된 메시는 이름이 매칭되는 모든 메시에 트랙을 생성한다.
   */
  private createVrmFilterClip(frames: number[][], fps: number, config: VrmFilterConfig | undefined, targetMeshNames?: string[]): AnimationClip {
    const times = frames.map((_, index) => index / fps);
    const tracks: KeyframeTrack[] = [];
    const meshes = targetMeshNames
      ? this.morphMeshes.filter(mesh => targetMeshNames.some(name => mesh.name.includes(name)))
      : this.morphMeshes;
    const allowed = config?.indices?.length ? new Set(config.indices) : null;
    const exclude = new Set(config?.excludeNames ?? []);
    const gains = config?.channelGains ?? {};

    const pushTrack = (mesh: Mesh, morphIndex: number, channel: number, gain: number) => {
      const values = frames.map(f => Math.min(1, Math.max(0, (f[channel] ?? 0) * gain)));
      tracks.push(new NumberKeyframeTrack(
        `${mesh.name}.morphTargetInfluences[${morphIndex}]`,
        times, values, InterpolateLinear
      ));
    };

    // 서버가 실제 보낸 채널 폭까지만 매핑 (52ch 구버전 응답이면 U/O 트랙은 생성 안 함)
    const frameWidth = frames[0]?.length ?? 0;

    meshes.forEach((mesh) => {
      const influences = mesh.morphTargetInfluences;
      if (!influences) return;

      if (config?.mappingTable && Object.keys(config.mappingTable).length) {
        // 명시 테이블 방식
        Object.entries(config.mappingTable).forEach(([serverIdxStr, morphIndex]) => {
          const channel = Number(serverIdxStr);
          if (channel >= frameWidth) return;
          if (allowed && !allowed.has(channel)) return;
          if (morphIndex === undefined || morphIndex >= influences.length) return;
          const name = SERVER_CHANNEL_NAMES[channel];
          if (name && exclude.has(name)) return;
          pushTrack(mesh, morphIndex, channel, (name && gains[name]) || 1);
        });
      } else {
        // 이름 기반 자동 매핑 (ARKit 52 + 'U','O' — 모프가 없는 이름은 자동 스킵)
        const dict = mesh.morphTargetDictionary;
        if (!dict) return;
        SERVER_CHANNEL_NAMES.forEach((name, channel) => {
          if (channel >= frameWidth) return;
          if (allowed && !allowed.has(channel)) return;
          if (exclude.has(name)) return;
          const morphIndex = dict[name];
          if (morphIndex === undefined || morphIndex >= influences.length) return;
          pushTrack(mesh, morphIndex, channel, gains[name] ?? 1);
        });
      }
    });

    if (!tracks.length && !this.naturalWarnedMissingTarget) {
      console.warn('[LipSync] vrm_filter: 매핑되는 모프를 찾지 못해 립싱크를 건너뜁니다');
      this.naturalWarnedMissingTarget = true;
    }
    return new AnimationClip("animation", -1, tracks);
  }

  private createNaturalDownmixClip(frames: number[][], fps: number, config: NaturalDownmixConfig): AnimationClip {
    const targets = config?.targets;
    if (!targets || frames.length === 0) return new AnimationClip("animation", -1, []);

    if (!this.naturalState) {
      this.naturalState = createNaturalState({ ...DEFAULT_NORM_PRIORS, ...config.normPriors });
    }
    const state = this.naturalState;

    // 청크 공백 휴리스틱 — stop() 을 안 거치고 새 발화가 시작된 경우 대비
    const now = performance.now();
    const gapMs = (config.resetGapSeconds ?? 2.0) * 1000;
    if (state.lastPlayAtMs > 0 && now - state.lastPlayAtMs > gapMs) {
      resetCausalState(state);
    }
    state.lastPlayAtMs = now;

    // 튜닝 패널(?tuning=1)의 런타임 오버라이드가 최우선 — 청크마다 읽어 즉시 반영
    const overrides = getNaturalRuntimeOverrides();
    const params = { ...DEFAULT_NATURAL_PARAMS, ...config.params, ...overrides?.params };
    const out = processNaturalChunk(frames, fps, state, params, {
      blinkEnabled: overrides?.blinkEnabled ?? config.blinkEnabled ?? false,
    });

    const mouthMesh = this.findMeshesByName(targets.mouthMeshName).find((m) =>
      m.morphTargetInfluences &&
      targets.jawMorphIndex < m.morphTargetInfluences.length &&
      targets.closeMorphIndex < m.morphTargetInfluences.length
    );
    const influences = mouthMesh?.morphTargetInfluences;
    if (!mouthMesh || !influences) {
      if (!this.naturalWarnedMissingTarget) {
        console.warn(`[LipSync] natural_downmix: 입 메시('${targets.mouthMeshName}') 또는 모프 인덱스를 찾지 못해 립싱크를 건너뜁니다`);
        this.naturalWarnedMissingTarget = true;
      }
      return new AnimationClip("animation", -1, []);
    }

    const tracks: KeyframeTrack[] = [];
    const times = frames.map((_, index) => index / fps);

    tracks.push(new NumberKeyframeTrack(
      `${mouthMesh.name}.morphTargetInfluences[${targets.jawMorphIndex}]`,
      times, Array.from(out.jaw), InterpolateLinear
    ));
    tracks.push(new NumberKeyframeTrack(
      `${mouthMesh.name}.morphTargetInfluences[${targets.closeMorphIndex}]`,
      times, Array.from(out.close), InterpolateLinear
    ));

    // Neutral 핀 — 클립이 재생되는 동안에도 1 유지 (상수 트랙)
    const neutralIdx = targets.neutralMorphIndex;
    if (config.pinNeutral !== false && neutralIdx !== undefined && neutralIdx < influences.length) {
      const endTime = times[times.length - 1];
      tracks.push(new NumberKeyframeTrack(
        `${mouthMesh.name}.morphTargetInfluences[${neutralIdx}]`,
        endTime > 0 ? [0, endTime] : [0],
        endTime > 0 ? [1, 1] : [1],
        InterpolateLinear
      ));
    }

    if (out.blink && targets.blinkMeshName && targets.blinkMorphIndices?.length) {
      const blinkValues = Array.from(out.blink);
      // 눈 메시는 프리미티브 분할로 여러 개일 수 있음 (Circle002_1~_3) — 전부 구동
      this.findMeshesByName(targets.blinkMeshName).forEach((blinkMesh) => {
        const blinkInfluences = blinkMesh.morphTargetInfluences;
        if (!blinkInfluences) return;
        targets.blinkMorphIndices!.forEach((blinkIdx) => {
          if (blinkIdx < blinkInfluences.length) {
            tracks.push(new NumberKeyframeTrack(
              `${blinkMesh.name}.morphTargetInfluences[${blinkIdx}]`,
              times, blinkValues, InterpolateLinear
            ));
          }
        });
      });
    }

    return new AnimationClip("animation", -1, tracks);
  }

  private processRangeMapping(blendshape: number[][], config: RangeMappingConfig): number[][] {
    const { sourceIndex, targetMappings, resetIndices } = config;

    // 소스 인덱스의 모든 값을 수집
    const sourceValues = blendshape.map(frame => frame[sourceIndex]);
    // 스택 오버플로우 방지: Math.min/max(...array) 대신 루프 사용
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < sourceValues.length; i++) {
      if (sourceValues[i] < min) min = sourceValues[i];
      if (sourceValues[i] > max) max = sourceValues[i];
    }
    const range = max - min;
    const step = range / targetMappings.length;

    // 구간 설정
    const intervals = targetMappings.map((mapping, index) => ({
      max: min + step * (index + 1),
      targetIndex: mapping.targetIndex
    }));

    // 각 프레임별로 값 변환
    return blendshape.map(frame => {
      const newFrame = [...frame];

      // 대상 인덱스들을 0으로 초기화
      resetIndices.forEach(i => newFrame[i] = 0);

      const sourceValue = frame[sourceIndex];
      // 구간에 따라 해당 인덱스에 1 설정
      const matchedInterval = intervals.find(interval => sourceValue <= interval.max);
      if (matchedInterval) {
        newFrame[matchedInterval.targetIndex] = 1;
      }

      return newFrame;
    });
  }

  private processFilter(blendshape: number[][], config: FilterConfig): number[][] {
    const { indices } = config;
    const transposedBlendShapes = transposeArray(blendshape);

    // 필터링된 인덱스만 유지, 나머지는 0으로 설정
    const filteredTransposed = transposedBlendShapes.map((values, index) => {
      return indices.includes(index) ? values : Array(values.length).fill(0);
    });

    return transposeArray(filteredTransposed);
  }

  /**
   * 눈 깜빡임 애니메이션을 자연스럽게 보간하는 함수
   * @param blendshape 원본 블렌드셰이프 데이터
   * @param fps 초당 프레임 수 (기본값: 30)
   * @returns 눈 깜빡임이 추가된 블렌드셰이프 데이터
   */
  private addBlinkAnimation(blendshape: number[][], fps: number = 30): number[][] {
    const blinkDurationSeconds = 0.5; // 깜빡임 지속시간 (초)
    const blinkIntervalSeconds = 3; // 깜빡임 간격 (초)

    const blinkDurationFrames = Math.floor(blinkDurationSeconds * fps); // 약 4-5프레임
    const blinkIntervalFrames = Math.floor(blinkIntervalSeconds * fps); // 약 90프레임

    return blendshape.map((frame, frameIndex) => {
      const newFrame = [...frame];

      // 깜빡임 주기 계산
      const cyclePosition = frameIndex % blinkIntervalFrames;

      // 애니메이션 마지막 프레임인지 확인
      const isLastFrame = frameIndex === blendshape.length - 1;

      // 깜빡임이 시작되는 지점인지 확인
      if (cyclePosition < blinkDurationFrames && !isLastFrame) {
        // 깜빡임 애니메이션 진행률 (0 ~ 1)
        const progress = cyclePosition / (blinkDurationFrames - 1);

        // 자연스러운 깜빡임을 위한 ease-in-out 보간
        // 0 -> 1 -> 0 의 부드러운 곡선
        let blinkValue: number;
        if (progress <= 0.5) {
          // 눈을 감는 단계 (0 -> 1)
          blinkValue = this.easeInOut(progress * 2);
        } else {
          // 눈을 뜨는 단계 (1 -> 0)
          blinkValue = this.easeInOut((1 - progress) * 2);
        }

        // 인덱스 8, 9에 깜빡임 값 적용
        newFrame[8] = blinkValue;
        newFrame[9] = blinkValue;
      } else {
        // 깜빡임이 없는 구간이거나 마지막 프레임인 경우 0으로 초기화
        newFrame[8] = 0;
        newFrame[9] = 0;
      }

      return newFrame;
    });
  }

  /**
   * Ease-in-out 보간 함수 (부드러운 애니메이션을 위함)
   * @param t 0~1 사이의 진행률
   * @returns 보간된 값 (0~1)
   */
  private easeInOut(t: number): number {
    return t * t * (3 - 2 * t);
  }
}